import {
  SecurityAssetSecurityType,
  AssetPriceType,
  SecurityAsset,
  SecurityFixedIncomeDetails,
  UserSharesEdgeFragment,
} from '@fintronners/react-api/src';

import {
  SECURITY_CATEGORY,
  STOCK_SECURITY_TYPES,
  ETF_SECURITY_TYPES,
  MUTUAL_FUND_SECURITY_TYPES,
  FIXED_INCOME_SECURITY_TYPES,
} from '../../screeners/constants';

import { NumberNoNaN } from '@fintronners/react-utils/src/numberUtilsTSX';
import type { SecurityCategory } from '@fintronners/internal-core/src/screeners/types';

/**
 * Gets the security category given a security type.
 */
export const getHoldingsCategoryBySecurityType = (securityType: SecurityAssetSecurityType) => {
  if (STOCK_SECURITY_TYPES.includes(securityType)) {
    return SECURITY_CATEGORY.STOCK;
  }

  if (ETF_SECURITY_TYPES.includes(securityType)) {
    return SECURITY_CATEGORY.ETF;
  }

  if (MUTUAL_FUND_SECURITY_TYPES.includes(securityType)) {
    return SECURITY_CATEGORY.MUTUAL_FUND;
  }

  if (FIXED_INCOME_SECURITY_TYPES.includes(securityType)) {
    return SECURITY_CATEGORY.FIXED_INCOME;
  }

  return undefined;
};

/**
 * Gets the price of a bond asset.
 */
export const getBondAssetPrice = (asset: SecurityAsset): number => {
  const lastPrices = asset.fixedIncomeMarket?.lastPrices;
  const parValue = (asset?.details as SecurityFixedIncomeDetails)?.parValue;

  const percentageOfPar = lastPrices?.find(
    (l) => l.type === AssetPriceType.AssetPriceTypePercentageOfPar,
  );

  return (NumberNoNaN(percentageOfPar?.price) / 100) * NumberNoNaN(parValue);
};

/**
 * Gets the asset price.
 */
export const getAssetPrice = (
  asset:
    | {
        securityType?: SecurityAssetSecurityType;
        details?:
          | {
              __typename?: string;
              netAssetValue?: number | string | null;
              netAssetValueChange?: number | string | null;
            }
          | null
          | undefined;
        market?: {
          last?: number | string | null;
        } | null;
      }
    | null
    | undefined,
) => {
  if (
    asset?.securityType === SecurityAssetSecurityType.MutualFund &&
    asset.details?.__typename === 'SecurityMutualFundDetails'
  ) {
    return NumberNoNaN(asset.details.netAssetValue);
  }

  return NumberNoNaN(asset?.market?.last);
};

/**
 * Gets the gain/loss of an asset.
 */
export const getAssetGainLoss = (
  asset:
    | {
        securityType?: SecurityAssetSecurityType;
        details?:
          | {
              __typename?: string;
              netAssetValue?: number | string | null;
              netAssetValueChange?: number | string | null;
              [key: string]: any;
            }
          | null
          | undefined;
        market?: {
          last?: number | string | null;
          previousDayClose?: number | string | null;
        } | null;
      }
    | null
    | undefined,
) => {
  let numericGainLoss, percentGainLoss;

  if (
    asset?.securityType === SecurityAssetSecurityType.MutualFund &&
    asset.details?.__typename === 'SecurityMutualFundDetails'
  ) {
    numericGainLoss = NumberNoNaN(asset.details.netAssetValueChange);
    percentGainLoss = numericGainLoss / NumberNoNaN(asset.details.netAssetValue);

    return { numericGainLoss, percentGainLoss };
  }

  const last = NumberNoNaN(asset?.market?.last);
  const previousDayClose = NumberNoNaN(asset?.market?.previousDayClose);

  numericGainLoss = last - previousDayClose;
  percentGainLoss = numericGainLoss / previousDayClose;

  return { numericGainLoss, percentGainLoss };
};

/**
 * Gets the total market value of all the security assets provided.
 */
export const getHoldingsTotalMarketValue = (
  assetNodes: UserSharesEdgeFragment[],
): number | undefined => {
  if (!assetNodes.length) return undefined;

  const totalAssetsAmount = assetNodes.reduce((acc, assetNode) => {
    const asset = assetNode.node?.asset as SecurityAsset;
    const isBond = asset.securityType === SecurityAssetSecurityType.FixedIncome;

    const balance = NumberNoNaN(assetNode.node?.balances?.activeBalance);
    const last = isBond ? getBondAssetPrice(asset) : getAssetPrice(asset);

    const price = NumberNoNaN(last);
    const total = balance * price;

    return acc + total;
  }, 0);

  return totalAssetsAmount;
};

/**
 * Gets Security Assets grouped by Security Type.
 */
export const getGroupedSecurityAssets = (
  assets: (UserSharesEdgeFragment | null)[] | null | undefined,
) => {
  const groupedSecurityAssets: Record<SecurityCategory, UserSharesEdgeFragment[]> = {
    [SECURITY_CATEGORY.STOCK]: [],
    [SECURITY_CATEGORY.ETF]: [],
    [SECURITY_CATEGORY.MUTUAL_FUND]: [],
    [SECURITY_CATEGORY.FIXED_INCOME]: [],
  };

  assets?.reduce((acc, item) => {
    const isSecurityAsset = item?.node?.asset?.__typename === 'SecurityAsset';
    if (!isSecurityAsset) return acc;

    const securityType = (item.node?.asset as SecurityAsset)?.securityType;
    const securityCategory = getHoldingsCategoryBySecurityType(securityType);

    if (!securityCategory) return acc;
    acc[securityCategory].push(item);

    return acc;
  }, groupedSecurityAssets);

  return groupedSecurityAssets;
};
